import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@repo/ui";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

const INTERESTS = [
  { value: "semi_detach", label: "Semi-detach บ้านเดี่ยว" },
  { value: "townhouse", label: "Townhouse/townhome/home office ทาวน์เฮ้าส์/ทาวน์โฮม/โฮออฟฟิศ" },
  { value: "condo", label: "Condominium คอนโดมิเนียม" },
  { value: "second_hand", label: "Second-hand บ้านมือ 2" },
  { value: "furniture", label: "Furniture สินค้าเบ็ดเตล็ด/เฟอร์นิเจอร์" },
  { value: "other", label: "Others อื่นๆ ระบุ" },
];

const BANKS = [
  "ธนาคารกรุงเทพ จำกัด (มหาชน)",
  "ธนาคารกรุงไทย จำกัด (มหาชน)",
  "ธนาคารกรุงศรีอยุธยา จำกัด (มหาชน)",
  "ธนาคารกสิกรไทย จำกัด (มหาชน)",
  "ธนาคารทหารไทยธนชาต จำกัด (มหาชน)",
  "ธนาคารซีไอเอ็มบี ไทย จำกัด (มหาชน)",
  "ธนาคารไทยพาณิชย์ จำกัด (มหาชน)",
  "ธนาคารยูโอบี จำกัด (มหาชน)",
  "ธนาคารทิสโก้ จำกัด (มหาชน)",
  "ธนาคารเกียรตินาคินภัทร จำกัด (มหาชน)",
  "ธนาคารแลนด์ แอนด์ เฮ้าส์ จำกัด (มหาชน)",
  "ธนาคารออมสิน",
  "ธนาคารอาคารสงเคราะห์",
  "ธนาคารอิสลามแห่งประเทศไทย",
];

const schema = z
  .object({
    name: z
      .string()
      .trim()
      .min(2, "Name must be at least 2 characters / ชื่อต้องมีอย่างน้อย 2 ตัวอักษร")
      .max(100, "Name is too long / ชื่อยาวเกินไป"),
    surname: z
      .string()
      .trim()
      .min(2, "Surname must be at least 2 characters / นามสกุลต้องมีอย่างน้อย 2 ตัวอักษร")
      .max(100, "Surname is too long / นามสกุลยาวเกินไป"),
    phone: z
      .string()
      .trim()
      .transform((val) => val.replace(/\D/g, "")) // Strip formatting before validation
      .pipe(
        z
          .string()
          .regex(
            /^0\d{9}$/,
            "Phone must be 10 digits starting with 0 / เบอร์โทรต้องเป็นตัวเลข 10 หลัก ขึ้นต้นด้วย 0"
          )
      ),
    email: z
      .string()
      .trim()
      .email("Invalid email format / รูปแบบอีเมลไม่ถูกต้อง")
      .max(255, "Email is too long / อีเมลยาวเกินไป"),
    age: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val || val.trim() === "") return true;
          const num = Number(val);
          return !isNaN(num) && num >= 1 && num <= 120;
        },
        { message: "Age must be between 1-120 / อายุต้องอยู่ระหว่าง 1-120" }
      ),
    interests: z
      .array(z.string())
      .min(1, "Please select at least one interest / กรุณาเลือกความสนใจอย่างน้อย 1 รายการ"),
    interestOther: z.string().trim().optional(),
    banks: z
      .array(z.string())
      .min(1, "Please select at least one bank / กรุณาเลือกธนาคารอย่างน้อย 1 แห่ง"),
  })
  .superRefine((data, ctx) => {
    // Validate that if "other" is selected, interestOther must be filled
    if (
      data.interests.includes("other") &&
      (!data.interestOther || data.interestOther.trim() === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please specify other interest / กรุณาระบุความสนใจอื่นๆ",
        path: ["interestOther"],
      });
    }
  });

type FormValues = z.infer<typeof schema>;

// Format phone number as user types: 0800000000 -> 080-000-0000
const formatPhoneNumber = (value: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, "");

  // Limit to 10 digits
  const limited = digits.slice(0, 10);

  // Format as 080-000-0000
  if (limited.length <= 3) return limited;
  if (limited.length <= 6) return `${limited.slice(0, 3)}-${limited.slice(3)}`;
  return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
};

export default function RegisterPage() {
  const navigate = useNavigate();
  const [successId, setSuccessId] = useState<number | null>(null);
  const [successName, setSuccessName] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: {
      name: "",
      surname: "",
      phone: "",
      email: "",
      age: "",
      interests: [],
      interestOther: "",
      banks: [],
    },
  });

  const watchInterests = form.watch("interests");

  const onSubmit = async (data: FormValues) => {
    setSubmitError(null);

    try {
      const res = await fetch("/api/registrations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...data,
          age: data.age && data.age.trim() ? Number(data.age) : undefined,
        }),
      });
      if (!res.ok) throw new Error("Failed");
      const json = (await res.json()) as { id?: number };
      setSuccessId(json.id ?? null);
      setSuccessName(data.name);
      form.reset();
    } catch {
      setSubmitError("การส่งข้อมูลล้มเหลว กรุณาลองใหม่อีกครั้ง / Submission failed");
    }
  };

  if (successId && successName) {
    return (
      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-xl">
          <CardHeader>
            <div className="flex flex-col gap-3">
              <div>
                <Button variant="outline" onClick={() => navigate("/")}>
                  Back
                </Button>
              </div>
              <CardTitle className="text-2xl text-center">
                ขอบคุณ {successName}! / Thank you {successName}!
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <p className="text-center text-sm text-muted-foreground">
              Your registration is complete. Save this screenshot or look up your QR code again via
              your phone number.
            </p>
            <div className="flex flex-col items-center gap-3">
              <img
                alt="QR Code"
                className="size-48"
                src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(String(successId))}`}
              />
              <div className="text-sm">ID: {successId}</div>
              <a className="text-sm underline" href="/lookup">
                Look up via phone number
              </a>
              <Button className="mt-2" onClick={() => navigate(`/coupon/${successId}`)}>
                ตอบคำถามเพิ่มเพื่อรับ Coupon
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center p-6">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex flex-col gap-3">
            <div>
              <Button variant="outline" onClick={() => navigate("/")}>
                Back
              </Button>
            </div>
            <CardTitle className="text-2xl text-center">
              ลงทะเบียนงาน 48th House & Condo Expo Bangkok
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name ชื่อ</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="ชื่อ" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="surname"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Surname นามสกุล</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="นามสกุล" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone เบอร์โทรศัพท์มือถือ</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          inputMode="tel"
                          placeholder="080-000-0000"
                          onChange={(e) => {
                            const formatted = formatPhoneNumber(e.target.value);
                            field.onChange(formatted);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email อีเมล</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" placeholder="hello@gmail.com" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="age"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Age อายุ (Optional)</FormLabel>
                      <FormControl>
                        <Input {...field} type="number" inputMode="numeric" placeholder="32" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="interests"
                render={() => (
                  <FormItem>
                    <FormLabel>Interest ความสนใจภายในงาน</FormLabel>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {INTERESTS.map((opt) => (
                        <FormField
                          key={opt.value}
                          control={form.control}
                          name="interests"
                          render={({ field }) => (
                            <FormItem className="flex items-center gap-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(opt.value)}
                                  onCheckedChange={(checked) => {
                                    return checked
                                      ? field.onChange([...field.value, opt.value])
                                      : field.onChange(
                                          field.value?.filter((value) => value !== opt.value)
                                        );
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-normal">{opt.label}</FormLabel>
                            </FormItem>
                          )}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {watchInterests?.includes("other") && (
                <FormField
                  control={form.control}
                  name="interestOther"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>โปรดระบุ Others</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="banks"
                render={() => (
                  <FormItem>
                    <FormLabel>Bank of interest สินเชื่อจากสถาบันการเงินที่สนใจ</FormLabel>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {BANKS.map((bank) => (
                        <FormField
                          key={bank}
                          control={form.control}
                          name="banks"
                          render={({ field }) => (
                            <FormItem className="flex items-center gap-3 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(bank)}
                                  onCheckedChange={(checked) => {
                                    return checked
                                      ? field.onChange([...field.value, bank])
                                      : field.onChange(
                                          field.value?.filter((value) => value !== bank)
                                        );
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-normal">{bank}</FormLabel>
                            </FormItem>
                          )}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {submitError && <p className="text-sm text-destructive">{submitError}</p>}

              <Button type="submit" disabled={form.formState.isSubmitting} className="w-full">
                {form.formState.isSubmitting ? "Submitting..." : "Register ลงทะเบียน"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
